import { z } from 'zod';

// GET /products/areas — catálogo de áreas para el selector global.
//
// Modelo (Backend PIM release 2026-08-21):
// - Un área es un cliente con lente propia sobre el catálogo.
// - `allowedFields` = scope: los pointers que el área LEE. El form solo pinta
//   controles cubiertos por esta lista (prefix match con `/` boundary).
//   Vacío = sin restricción (lente abierta).
// - `readOnlyFields` = permiso de escritura: pointers que el área lee pero el
//   admin NO puede editar. El back los marca `editable: false, reason:
//   'areaReadOnly'` en el mapa `editability` del detalle — el front SOLO usa
//   este array para saber "qué campos habría que consultar como readOnly" a
//   nivel config; la decisión editable/readonly per SKU sigue viniendo del
//   mapa `editability`.
//
// - `lensFields` = la LENTE resuelta del área: `allowedFields` ∪ los pointers
//   con prioridad de fuentes configurada, deduplicado y normalizado por el
//   back. Es la lista que decide QUÉ SE PINTA en el editor de atributos; el
//   área configura prioridad para campos que no publica en su API B2B, y
//   filtrar por `allowedFields` los escondía (medido en producción para
//   `vtex`: allowedFields 15, lente real 26).
//
// Las tres listas contestan preguntas distintas y las tres siguen en uso:
// `lensFields` qué se pinta, `readOnlyFields` qué puede editar el admin,
// `allowedFields` cuál es la superficie de la API B2B del área.
//
// `writableFields` NO llega — es el contrato B2B del cliente (permiso de su
// propia API key), no aplica al admin. `hiddenFields` fue reemplazado por
// `readOnlyFields` en el release: el back ya no oculta keys, las muestra
// readonly.
//
// Release backend 2026-09-21 (semántica de área nueva, LIVE en producción):
// un área ahora lee SOLO sus fuentes configuradas, no todo el catálogo.
// Sumó dos campos:
// - `sources`: fuentes que el área efectivamente consulta. Informativo hoy
//   (ninguna lógica del front rama por esto todavía) — sin `.default()`
//   porque ausente (backend viejo) y `[]` (área que no lee nada) significan
//   cosas distintas y no hay que confundirlas.
// - `canEditMaster`: si el área puede escribir en master. Gatea los botones
//   de guardar/editar del detalle (ver `resolveCanEditMaster` en
//   `features/products/utils.ts`). `.default(true)` a propósito: ausente
//   = backend viejo sin este campo, y la pantalla debe seguir permitiendo
//   editar como siempre (fail-open), no bloquear todo de golpe.
export const ProductAreaSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  allowedFields: z.array(z.string()).optional().default([]),
  readOnlyFields: z.array(z.string()).optional().default([]),
  // `.optional()` Y `.nullable()`, SIN `.default()`: ausente y null significan
  // cosas distintas y el parseo tiene que preservar la diferencia.
  // - ausente  = back sin deployar todavía -> el front cae al comportamiento
  //   viejo y filtra por `allowedFields`.
  // - null     = área SIN restricción, toda la lente canónica (caso `master`,
  //   que trae `allowedFields` vacío). Convertirlo a `[]` dejaría la pantalla
  //   en blanco.
  // - array    = la lente resuelta.
  lensFields: z.array(z.string()).nullable().optional(),
  sources: z.array(z.string()).optional(),
  canEditMaster: z.boolean().optional().default(true),
});
export type ProductArea = z.infer<typeof ProductAreaSchema>;

export const ProductAreasResponseSchema = z.array(ProductAreaSchema);
export type ProductAreasResponse = z.infer<typeof ProductAreasResponseSchema>;
